import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { ChartModule } from 'primeng/chart';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccountService } from '../../services/account.service';
import { ISubscription } from '../account-subscriptions/account-subscriptions';

// Dashboard interfaces
export interface IDashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  totalAccounts: number;
  pendingInvitations: number;
}

export interface IRecentActivity {
  id: string;
  type: 'user_login' | 'subscription_created' | 'user_invited' | 'profile_updated';
  message: string;
  timestamp: string;
  user?: string;
  icon: string;
  color: string;
}

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    BadgeModule,
    AvatarModule,
    ChartModule,
    ProgressBarModule
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css'
})
export class Dashboard implements OnInit {
  // User role and profile
  currentUser: any = {
    fullName: 'Raveendar Kumar',
    email: 'raveendar@simplygenie.com',
    role: 'Admin', // This would come from auth service
    profileImage: null
  };

  // Dashboard data
  stats: IDashboardStats = {
    totalUsers: 0,
    activeUsers: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0,
    totalAccounts: 0,
    pendingInvitations: 0
  };

  recentSubscriptions: ISubscription[] = [];
  recentUsers: any[] = [];
  recentActivities: IRecentActivity[] = [];

  // Chart data
  userGrowthChartData: any;
  revenueChartData: any;
  subscriptionChartData: any;

  // Chart options
  chartOptions: any;

  constructor(private accountService: AccountService) {}

  ngOnInit() {
    this.loadDashboardData();
    this.initializeCharts();
  }

  loadDashboardData() {
    // Mock data - in real app, this would come from API calls
    this.stats = {
      totalUsers: 24,
      activeUsers: 18,
      totalSubscriptions: 8,
      activeSubscriptions: 6,
      monthlyRevenue: 15000,
      yearlyRevenue: 180000,
      totalAccounts: 12,
      pendingInvitations: 3
    };

    this.recentSubscriptions = [
      {
        product: 'core',
        name: 'Core Basic',
        type: 'subscription',
        billingCycle: 'monthly',
        baseFee: 3000,
        features: ['Access to core features', 'Basic reporting'],
        isActive: true,
        startDate: '2024-01-01',
        nextBillingDate: '2024-02-01',
        status: 'active'
      },
      {
        product: 'pulse',
        name: 'Pulse Basic',
        type: 'subscription',
        billingCycle: 'monthly',
        baseFee: 2000,
        features: ['Scheme management', 'Basic insights'],
        isActive: true,
        startDate: '2024-01-15',
        nextBillingDate: '2024-02-15',
        status: 'active'
      }
    ];

    this.recentUsers = [
      {
        id: '1',
        firstName: 'Priya',
        lastName: 'Sharma',
        userEmail: 'priya@simplygenie.com',
        mobileNumber: '+91 87654 32109',
        userRole: 'Manager',
        isActive: true,
        lastLogin: '2024-01-15T10:30:00Z',
        createdAt: '2023-08-15T10:00:00Z',
        tenantAccessList: []
      },
      {
        id: '2',
        firstName: 'Amit',
        lastName: 'Singh',
        userEmail: 'amit@simplygenie.com',
        mobileNumber: '+91 76543 21098',
        userRole: 'User',
        isActive: true,
        lastLogin: '2024-01-14T15:45:00Z',
        createdAt: '2023-10-01T08:30:00Z',
        tenantAccessList: []
      }
    ];

    this.recentActivities = [
      {
        id: '1',
        type: 'user_login',
        message: 'Priya Sharma logged in',
        timestamp: '2024-01-15T10:30:00Z',
        user: 'Priya Sharma',
        icon: 'pi pi-sign-in',
        color: 'text-green-600'
      },
      {
        id: '2',
        type: 'subscription_created',
        message: 'New Core Basic subscription created',
        timestamp: '2024-01-15T09:15:00Z',
        icon: 'pi pi-plus-circle',
        color: 'text-blue-600'
      },
      {
        id: '3',
        type: 'user_invited',
        message: 'Invitation sent to new user',
        timestamp: '2024-01-14T16:20:00Z',
        user: 'Amit Singh',
        icon: 'pi pi-user-plus',
        color: 'text-purple-600'
      },
      {
        id: '4',
        type: 'profile_updated',
        message: 'User profile updated',
        timestamp: '2024-01-14T14:10:00Z',
        user: 'Raveendar Kumar',
        icon: 'pi pi-user-edit',
        color: 'text-orange-600'
      }
    ];
  }

  initializeCharts() {
    // User Growth Chart
    this.userGrowthChartData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'New Users',
          data: [12, 19, 3, 5, 2, 3],
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 2,
          tension: 0.4
        }
      ]
    };

    // Revenue Chart
    this.revenueChartData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [
        {
          label: 'Monthly Revenue',
          data: [12000, 15000, 18000, 14000, 16000, 15000],
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 2,
          tension: 0.4
        }
      ]
    };

    // Subscription Chart
    this.subscriptionChartData = {
      labels: ['Active', 'Inactive', 'Cancelled', 'Expired'],
      datasets: [
        {
          data: [6, 1, 0, 1],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(156, 163, 175, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(245, 158, 11, 0.8)'
          ],
          borderWidth: 0
        }
      ]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        },
        x: {
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      }
    };
  }

  // Utility methods
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getInitials(firstName: string, lastName: string): string {
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getRoleBadgeClass(role: string): string {
    switch (role) {
      case 'Admin': return 'bg-red-100 text-red-800';
      case 'Manager': return 'bg-blue-100 text-blue-800';
      case 'User': return 'bg-green-100 text-green-800';
      case 'Guest': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  // Check if user is admin
  get isAdmin(): boolean {
    return this.currentUser.role === 'Admin';
  }

  // Get user activity percentage
  getUserActivityPercentage(): number {
    return Math.round((this.stats.activeUsers / this.stats.totalUsers) * 100);
  }

  // Get subscription activity percentage
  getSubscriptionActivityPercentage(): number {
    return Math.round((this.stats.activeSubscriptions / this.stats.totalSubscriptions) * 100);
  }
}
